// src/views/MintView.jsx - UPDATED WITH WALLET LIMITS
import React, { useState } from 'react';
import { ArrowRight, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import NFTSimulator from '../components/NFTSimulator/NFTSimulator';

export const MintView = ({ stats, saleActive, loading, mint, isConnected, connectWallet, nftBalance }) => {
  const [quantity, setQuantity] = useState(1);
  
  // Determine sale status
  const isSoldOut = stats.totalSupply >= 10000;
  const saleNotStarted = !saleActive && stats.totalSupply === 0;
  const salePaused = !saleActive && stats.totalSupply > 0 && !isSoldOut;
  const walletAtLimit = nftBalance >= 10;
  const walletCanMint = 10 - nftBalance;
  
  // Adjust max quantity based on wallet limit
  const maxMintQuantity = Math.min(10, walletCanMint);
  
  return (
    <div className="space-y-12">
      {/* NFT Simulator Section */}
      <div>
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-white font-mono uppercase tracking-tight">
          EXPERIENCE THE ART
        </h2>
        <NFTSimulator />
      </div>
      
      {/* Mint Section */}
      <div>
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-white font-mono uppercase tracking-tight">
          MINT YOUR DUNK
        </h2>
        
        {/* Wallet At Limit Warning */}
        {isConnected && walletAtLimit && !isSoldOut && (
          <div className="bg-orange-500 text-white p-6 border-4 border-white flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#ff0066]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div>
              <div className="font-black text-2xl mb-2 uppercase">
                🔒 WALLET LIMIT REACHED
              </div>
              <div className="text-base opacity-90 mb-2">
                This wallet owns {nftBalance} NFTs. Maximum 10 per wallet.
              </div>
              <div className="text-sm font-bold">
                Use a different wallet to mint more Dunk Poge.
              </div>
            </div>
          </div>
        )}
        
        {/* Sale Status Warnings */}
        {isSoldOut && (
          <div className="bg-yellow-400 text-black p-6 border-4 border-black flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#000]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div className="flex-1">
              <div className="font-black text-2xl mb-3 uppercase">
                🎉 SOLD OUT
              </div>
              <div className="text-base mb-4 font-bold">
                All 10,000 Dunk Poge NFTs have been minted. Find them on secondary markets:
              </div>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="https://opensea.io/collection/dunkpoge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 border-4 border-black font-black uppercase flex items-center gap-2 hover:bg-gray-900 transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[6px_6px_0_0_#fff] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  OpenSea <ExternalLink size={16} />
                </a>
                <a 
                  href="https://blur.io/collection/dunkpoge" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 border-4 border-black font-black uppercase flex items-center gap-2 hover:bg-gray-900 transition-all shadow-[4px_4px_0_0_#fff] hover:shadow-[6px_6px_0_0_#fff] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  Blur <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        )}

        {saleNotStarted && (
          <div className="bg-blue-500 text-white p-6 border-4 border-white flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#00ff00]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div>
              <div className="font-black text-2xl mb-2 uppercase">
                ⏳ SALE NOT STARTED
              </div>
              <div className="text-base opacity-90">
                Minting has not begun yet. Check back soon or follow our announcements.
              </div>
            </div>
          </div>
        )}

        {salePaused && (
          <div className="bg-orange-500 text-white p-6 border-4 border-white flex items-start gap-4 mb-8 font-mono shadow-[8px_8px_0_0_#ff0066]">
            <AlertCircle size={32} className="flex-shrink-0" />
            <div>
              <div className="font-black text-2xl mb-2 uppercase">
                ⏸️ SALE PAUSED
              </div>
              <div className="text-base opacity-90">
                Minting is temporarily disabled. {stats.totalSupply}/10,000 have been minted. Check back later.
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Mint Info Card */}
          <div className="bg-white text-black p-8 border-4 border-green-400 relative font-mono shadow-[12px_12px_0_0_#00ff00]">
            <h3 className="text-3xl font-black mb-6 uppercase">
              MINT INFO
            </h3>
            
            {/* Stats Grid */}
            <div className="mb-8 space-y-4">
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">STATUS:</span>
                <span className={`text-xl font-black uppercase ${
                  isSoldOut ? 'text-yellow-600' : 
                  saleActive ? 'text-green-500' : 
                  'text-red-500'
                }`}>
                  {isSoldOut ? '🎉 SOLD OUT' : saleActive ? '✓ LIVE' : '✗ PAUSED'}
                </span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">PRICE:</span>
                <span className="text-2xl font-black">{stats.price} ETH</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">MINTED:</span>
                <span className="text-2xl font-black">{stats.totalSupply}/10,000</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b-2 border-black">
                <span className="font-bold">YOU OWN:</span>
                <span className="text-2xl font-black">{nftBalance}/10</span>
              </div>
              
              {isConnected && !walletAtLimit && !isSoldOut && (
                <div className="bg-green-100 border-2 border-green-500 p-3 text-center">
                  <span className="font-bold text-green-800">
                    You can mint {walletCanMint} more {walletCanMint === 1 ? 'NFT' : 'NFTs'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Quantity Selector - Hidden if sold out or wallet at limit */}
            {!isSoldOut && !walletAtLimit && (
              <>
                <div className="mb-6">
                  <label className="block font-black mb-3 text-lg uppercase">
                    QUANTITY (MAX {maxMintQuantity}):
                  </label>
                  <input 
                    type="number" 
                    min="1" 
                    max={maxMintQuantity}
                    value={quantity} 
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setQuantity(Math.min(maxMintQuantity, Math.max(1, value)));
                      } else {
                        setQuantity(1);
                      }
                    }}
                    className="w-full p-4 font-black text-2xl border-4 border-black bg-white text-black text-center mb-2 font-mono"
                    disabled={!saleActive || !isConnected}
                  />
                  <div className="font-black text-center text-gray-600">
                    TOTAL: {(parseFloat(stats.price) * quantity).toFixed(3)} ETH
                  </div>
                </div>
                
                {/* Mint Button */}
                {!isConnected ? (
                  <button 
                    onClick={connectWallet}
                    className="w-full font-black text-xl py-5 border-4 border-white flex items-center justify-center gap-3 uppercase font-mono transition-all duration-200 bg-blue-600 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[10px_10px_0_0_#000] shadow-[8px_8px_0_0_#000] text-white"
                  >
                    CONNECT WALLET
                    <ArrowRight size={24} />
                  </button>
                ) : (
                  <button 
                    onClick={() => mint(quantity)} 
                    disabled={loading || !saleActive}
                    className={`w-full font-black text-xl py-5 border-4 border-white flex items-center justify-center gap-3 uppercase font-mono transition-all duration-200 ${
                      loading || !saleActive
                        ? 'bg-gray-600 cursor-not-allowed opacity-70'
                        : 'bg-blue-600 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[10px_10px_0_0_#000]'
                    } shadow-[8px_8px_0_0_#000] text-white`}
                  >
                    {loading ? 'PROCESSING...' : !saleActive ? 'SALE NOT ACTIVE' : `MINT ${quantity} ${quantity === 1 ? 'NFT' : 'NFTS'}`}
                    {!loading && saleActive && <ArrowRight size={24} />}
                  </button>
                )}
                
                <div className="mt-3 text-xs text-center text-gray-600 font-bold">
                  10 NFTs maximum per wallet • Random mint
                </div>
              </>
            )}

            {/* Sold Out CTA */}
            {isSoldOut && (
              <div className="space-y-4">
                <div className="bg-yellow-400 text-black p-4 border-4 border-black text-center font-black text-lg uppercase">
                  PRIMARY SALE COMPLETE
                </div>
                <a 
                  href="https://opensea.io/collection/dunkpoge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-black text-white font-black text-xl py-5 border-4 border-black flex items-center justify-center gap-3 uppercase font-mono transition-all duration-200 hover:bg-gray-900 shadow-[8px_8px_0_0_#00ff00] hover:shadow-[10px_10px_0_0_#00ff00] hover:-translate-x-0.5 hover:-translate-y-0.5"
                >
                  VIEW ON OPENSEA
                  <ExternalLink size={24} />
                </a>
              </div>
            )}
            
            {/* Wallet At Limit CTA */}
            {walletAtLimit && !isSoldOut && (
              <div className="space-y-4">
                <div className="bg-orange-500 text-white p-4 border-4 border-white text-center font-black text-lg uppercase">
                  WALLET LIMIT: {nftBalance}/10
                </div>
                <div className="text-center text-sm font-bold text-gray-700">
                  Connect a different wallet to mint more Dunk Poge
                </div>
              </div>
            )}
            
            {/* Badge */}
            <div className="absolute -bottom-5 -right-5 bg-green-400 text-black px-6 py-3 font-black border-4 border-black rotate-3 shadow-[6px_6px_0_0_#000] text-sm uppercase font-mono tracking-wide z-10">
              DUNK POGE IS FOREVER
            </div>
          </div>

{/* Info Card */}
<div className="bg-blue-600 text-white p-8 border-4 border-white relative font-mono shadow-[0_0_30px_rgba(0,255,0,0.3),8px_8px_0_0_#fff]">
  {/* Info Badge */}
  <div className="absolute -top-5 left-6 bg-green-400 text-black px-4 py-2 font-black border-4 border-black shadow-[4px_4px_0_0_#000] text-sm uppercase font-mono">
    FULLY ON-CHAIN • 264 YEARS
  </div>
  
  <ul className="mt-4 space-y-4">
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>What Punks wanted to be.</strong> Full-color SVG, infinite resolution, 2B+ combos.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Stake for 264 years.</strong> Earn $POGE. 9 tokens/day → 1/day taper rewards early stakers.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Art lives in contract.</strong> No IPFS, no servers, no dependencies — Ethereum or nothing.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Immutable contracts.</strong> No owner, no admin, no pause — code is law.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Random mint.</strong> You can't pick. 10,000 supply. 0.005 ETH.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Free art simulator.</strong> Download unlimited. Mint costs ownership.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Achievements unlock forever.</strong> Early Adopter. Diamond Paws. Poge Whale.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Static frontend on Arweave.</strong> No backend, no database, no team dependency.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Punk spirit, Doge energy.</strong> CryptoPunks meet meme culture on-chain.
      </span>
    </li>
    <li className="flex items-start gap-3">
      <CheckCircle size={24} className="text-green-400 flex-shrink-0" />
      <span className="text-lg">
        <strong>Designed to outlive everything.</strong> Your great-grandchildren can still stake.
      </span>
    </li>
  </ul>
  
  {/* Decorative Element */}
  <div className="absolute bottom-4 right-4 text-xs opacity-50 italic uppercase tracking-widest">
    Much Permanence • Very Immutable • Wow
  </div>
</div>
        </div>
      </div>
    </div>
  );
};